import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import ImageUploader from './ImageUploader';
import RichTextEditor from './RichTextEditor';
import Toast from './Toast';
import { getSections, updateSection } from '../../services/api';
import { triggerDataRefresh } from '../../context/SiteDataContext';
import './admin.css';

const AuthorEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [formData, setFormData] = useState({
    id: 'main-author',
    image: '',
    title: '',
    subtitle: '',
    text: '',
    description: '',
    social: [],
  });

  useEffect(() => {
    loadAuthor();
  }, []);

  const loadAuthor = async () => {
    try {
      const response = await getSections(true);
      const author = response.authorConfig;
      if (author) {
        setFormData({
          id: author.id || 'main-author',
          image: author.image || '',
          title: author.title || '',
          subtitle: author.subtitle || '',
          text: author.text || '',
          description: author.description || '',
          social: author.social || [],
        });
      }
    } catch (err) {
      setToast({ message: err.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSocialChange = (index, field, value) => {
    const newSocial = [...formData.social];
    newSocial[index] = { ...newSocial[index], [field]: value };
    setFormData(prev => ({ ...prev, social: newSocial }));
  };

  const addSocialLink = () => {
    setFormData(prev => ({
      ...prev,
      social: [...prev.social, { icon: '', link: '' }],
    }));
  };

  const removeSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      social: prev.social.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setToast(null);
    setSaving(true);

    try {
      await updateSection(formData.id || 'main-author', 'authorConfig', formData);
      setToast({ message: '✓ Profil autrice enregistré !', type: 'success' });
      triggerDataRefresh();
    } catch (err) {
      setToast({ message: err.message || 'Erreur lors de l\'enregistrement', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">
          <div className="spinner"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <div>
            <h1>L'autrice</h1>
            <p>Modifier les informations de la section autrice</p>
          </div>
        </header>

        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="admin-form-grid">
            <div className="admin-form-group">
              <label>Nom</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="Aïssatou TANDIAN"
              />
            </div>

            <div className="admin-form-group">
              <label>Sous-titre</label>
              <input
                type="text"
                value={formData.subtitle}
                onChange={(e) => handleChange('subtitle', e.target.value)}
                placeholder="L'autrice"
              />
            </div>

            <div className="admin-form-group full-width">
              <label>Style / Spécialité</label>
              <input
                type="text"
                value={formData.text}
                onChange={(e) => handleChange('text', e.target.value)}
                placeholder="Style: Roman, fiction"
              />
            </div>

            <div className="admin-form-group full-width">
              <label>Biographie</label>
              <RichTextEditor
                value={formData.description}
                onChange={(value) => handleChange('description', value)}
                placeholder="Biographie de l'autrice..."
              />
            </div>

            <div className="admin-form-group full-width">
              <label>Photo</label>
              <ImageUploader
                currentImage={formData.image}
                onUpload={(url) => handleChange('image', url)}
              />
            </div>
          </div>

          <hr className="admin-divider" />

          <h3>Réseaux sociaux</h3>
          <div className="admin-social-links">
            {formData.social.map((social, index) => (
              <div key={index} className="admin-social-item" style={{ 
                display: 'flex', 
                gap: '12px', 
                alignItems: 'flex-end',
                marginBottom: '16px',
                padding: '16px',
                background: '#f8f9fa',
                borderRadius: '8px'
              }}>
                <div className="admin-form-group" style={{ flex: 1 }}>
                  <label>Icône (URL)</label>
                  <input
                    type="text"
                    value={social.icon}
                    onChange={(e) => handleSocialChange(index, 'icon', e.target.value)}
                    placeholder="assets/icons/facebook.svg"
                  />
                </div>
                <div className="admin-form-group" style={{ flex: 2 }}>
                  <label>Lien</label>
                  <input
                    type="url"
                    value={social.link}
                    onChange={(e) => handleSocialChange(index, 'link', e.target.value)}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeSocialLink(index)}
                  className="admin-btn admin-btn-sm admin-btn-danger"
                >
                  Supprimer
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addSocialLink}
              className="admin-btn admin-btn-secondary"
            >
              + Ajouter un réseau social
            </button>
          </div>

          <div className="admin-form-actions">
            <button
              type="submit"
              className="admin-btn admin-btn-primary"
              disabled={saving}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AuthorEditor;

